import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { compressImage, compressionPresets } from "../utils/imageCompression";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicAuthContext = createContext(null);

// Create axios instance with credentials for httpOnly cookies
const authAxios = axios.create({
  baseURL: API,
  withCredentials: true,  // Send cookies automatically
});

export const usePublicAuth = () => {
  const context = useContext(PublicAuthContext);
  if (!context) {
    throw new Error("usePublicAuth must be used within a PublicAuthProvider");
  }
  return context;
};

// Helper to format API errors
const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const PublicAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
  const [loading, setLoading] = useState(true);

  // Check auth status on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Verify auth by calling /me endpoint (cookie will be sent automatically)
      const response = await authAxios.get("/public/auth/me");
      setUser(response.data);
      // Also update localStorage user for UI purposes
      localStorage.setItem("user", JSON.stringify(response.data));
    } catch (err) {
      // Not authenticated or token invalid
      localStorage.removeItem("user");
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password, name) => {
    try {
      const response = await axios.post(`${API}/public/auth/register`, { email, password, name });
      return { success: true, data: response.data };
    } catch (err) {
      return {
        success: false,
        error: formatApiError(err.response?.data?.detail) || err.message,
      };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/unified-login`, { email, password }, {
        withCredentials: true  // Include cookies
      });
      const data = response.data;
      
      // Store only user info in localStorage (token is in httpOnly cookie)
      localStorage.setItem("user", JSON.stringify({
        id: data.id,
        email: data.email,
        name: data.name,
        role: data.role,
        user_type: data.user_type,
        credits: data.credits || 0
      }));
      
      setUser(data);
      return { success: true, data };
    } catch (err) {
      return {
        success: false,
        error: formatApiError(err.response?.data?.detail) || err.message,
      };
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to clear httpOnly cookie
      await authAxios.post("/auth/logout");
    } catch (err) {
      // Logout errors are non-critical - continue with local cleanup
      console.error("Logout API call failed:", err.message);
    }
    localStorage.removeItem("user");
    setUser(false);
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await authAxios.put("/public/profile", profileData);
      setUser((prev) => ({ ...prev, ...response.data }));
      return { success: true, data: response.data };
    } catch (err) {
      return {
        success: false,
        error: formatApiError(err.response?.data?.detail) || err.message,
      };
    }
  };

  const uploadAvatar = async (file) => {
    try {
      // Compress image before upload
      const compressedFile = await compressImage(file, compressionPresets.avatar);
      
      const formData = new FormData();
      formData.append("file", compressedFile);
      const response = await authAxios.post("/public/profile/avatar", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUser((prev) => ({ ...prev, profile_image: response.data.profile_image }));
      return { success: true, data: response.data };
    } catch (err) {
      return {
        success: false,
        error: formatApiError(err.response?.data?.detail) || err.message,
      };
    }
  };

  const uploadHeader = async (file) => {
    try {
      // Compress image before upload
      const compressedFile = await compressImage(file, compressionPresets.header);
      
      const formData = new FormData();
      formData.append("file", compressedFile);
      const response = await authAxios.post("/public/profile/header", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUser((prev) => ({ ...prev, header_image: response.data.header_image }));
      return { success: true, data: response.data };
    } catch (err) {
      return {
        success: false,
        error: formatApiError(err.response?.data?.detail) || err.message,
      };
    }
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    register,
    login,
    logout,
    updateProfile,
    uploadAvatar,
    uploadHeader,
    refreshUser,
  };

  return (
    <PublicAuthContext.Provider value={value}>
      {children}
    </PublicAuthContext.Provider>
  );
};

export default PublicAuthContext;
